import { ButtonStyle, ControlSize, ControlType, PageMode, Property } from "../../../base/types";
import { IControlAttributes, IPageLink } from "../../../base/models";
import { Registry } from "../../../base/register"
import { useGetProperties } from "../../../base/hooks";
import { PaymentButtonProperties } from "./properties";
import { AMBankButton, FlywireButton, HdfcButton, NTTButton, PayTmButton, PayUButton, RazorPayButton } from "../pg-integration";
import { useAuth } from "../../../../modules/auth";
import { MUIIcon, Spinner } from "../../../widgets";
import { KTSVG } from "../../../../helpers";
import { useState } from "react";
import { useUploadDocument } from "../../../services/data/sharepoint";
import { useCreateEntityChecklistItemMutation } from "../document-checklist/services/entityChecklistItemsApi";
import clsx from "clsx";
import { ICurrencyEntity, IDeferment, useCreateDefermentMutation, useCreatePaymentMutation, useDeleteLineMutation, useGetCurrencyCodeQuery, useUpdateDefermentLineMutation } from "./properties/paymentButtonApi";
import { PageLinkHelper } from "../../../base/helpers";
import { useNavigate } from "react-router-dom";
import moment from "moment";

export const PaymentButtonComponent = (props: IControlAttributes) => {
  const { control } = props;
  const { properties } = useGetProperties({ bindings: control.bindings });
  const enableOnline: boolean | undefined = properties[Property.EnableOnline];
  const enableOffline: boolean | undefined = properties[Property.EnableOffline];
  const enableDeferment: boolean | undefined = properties[Property.EnableDeferment];
  const enableRazorPay: boolean | undefined = properties[Property.EnableRazorPay];
  const enableHDFC: boolean | undefined = properties[Property.EnableHDFC];
  const enablePayU: boolean | undefined = properties[Property.EnablePayU];
  const enablePaytm: boolean | undefined = properties[Property.EnablePaytm];
  const enableFlywire: boolean | undefined = properties[Property.EnableFlywire];
  const enableNTT: boolean | undefined = properties[Property.EnableNTT];
  const enableAMBank: boolean | undefined = properties[Property.EnableAMBANK];
  const label: string | undefined = properties[Property.Label];
  const orderNo: string | undefined = properties[Property.OrderNo];
  const enrolmentNo: string | undefined = properties[Property.EnrolmentNo];
  const amount: number | undefined = properties[Property.Amount];
  const currency: string | undefined = properties[Property.Currency];
  const icon: string | undefined = properties[Property.MaterialIcon];
  const tooltip: string | undefined = properties[Property.Tooltip];
  const disabled: boolean | undefined = properties[Property.Disabled];
  const style: ButtonStyle | undefined = properties[Property.BackgroundStyle];
  const size: ControlSize | undefined = properties[Property.Size];
  const successPage: IPageLink | undefined = properties[Property.SuccessPage];
  const failedPage: IPageLink | undefined = properties[Property.FailedPage];
  const entity: string | undefined = properties[Property.Entity];
  const entityId: string | undefined = properties[Property.EntityId];
  const tutionFee: number | undefined = properties[Property.TutionFee];
  const hostelFee: number | undefined = properties[Property.HostelFee];
  const otherFee: number | undefined = properties[Property.OtherFee];
  const relativePath: string | undefined = properties[Property.RelativePath];
  const tableSchema: string | undefined = properties[Property.TableSchema];
  const [paymentMode, setPaymentMode] = useState<string>('online');

  if (enableOnline && !enableOffline && !enableDeferment) {
    return (
      <OnlinePayment
        enableOnline={enableOnline}
        enableRazorPay={enableRazorPay}
        enablePayU={enablePayU}
        enablePaytm={enablePaytm}
        enableFlywire={enableFlywire}
        enableNTT={enableNTT}
        enableHDFC={enableHDFC}
        enableAMBank={enableAMBank}
        label={label}
        orderNo={orderNo}
        enrolmentNo={enrolmentNo}
        amount={amount}
        currency={currency}
        icon={icon}
        tooltip={tooltip}
        disabled={disabled}
        style={style}
        size={size}
        successPage={successPage}
        failedPage={failedPage}
        entity={entity}
        entityId={entityId}
        tutionFee={tutionFee}
        hostelFee={hostelFee}
        otherFee={otherFee}
        relativePath={relativePath}
      />
    );
  }

  if (!enableOnline && enableOffline && !enableDeferment) {
    return (
      <OfflinePayment
        relativePath={relativePath}
        systemId={tableSchema}
        successPage={successPage}
      />
    );
  }

  if (enableDeferment && !enableOnline && !enableOffline) {
    return (
      <DefermentPayment
        relativePath={relativePath}
        systemId={tableSchema}
        successPage={successPage}
      />
    );
  }

  return (
    <div className="mt-5 mb-5">
      <div className="d-flex align-items-center card-header">
        <h3>Select Payment Mode</h3>
      </div>
      <div className="card-body">
        {enableOnline && <div className="form-check">
          <input
            type="radio"
            id="online"
            name="paymentMode"
            value="online"
            className="form-check-input"
            checked={paymentMode === 'online'}
            onChange={() => setPaymentMode('online')}
          />
          <label htmlFor="online" className="form-check-label">
            Online
          </label>
        </div>}
        {enableOffline && <div className="form-check mt-2">
          <input
            type="radio"
            id="offline"
            name="paymentMode"
            value="offline"
            className="form-check-input"
            checked={paymentMode === 'offline'}
            onChange={() => setPaymentMode('offline')}
          />
          <label htmlFor="offline" className="form-check-label">
            Offline
          </label>
        </div>}
        {enableDeferment && <div className="form-check mt-2">
          <input
            type="radio"
            id="deferment"
            name="paymentMode"
            value="deferment"
            className="form-check-input"
            checked={paymentMode === 'deferment'}
            onChange={() => setPaymentMode('deferment')}
          />
          <label htmlFor="deferment" className="form-check-label">
            Deferment
          </label>
        </div>}

        <div>
          {paymentMode === 'online' ? (
            <OnlinePayment
              enableOnline={enableOnline}
              enableRazorPay={enableRazorPay}
              enablePayU={enablePayU}
              enablePaytm={enablePaytm}
              enableFlywire={enableFlywire}
              enableNTT={enableNTT}
              enableHDFC={enableHDFC}
              enableAMBank={enableAMBank}
              label={label}
              orderNo={orderNo}
              enrolmentNo={enrolmentNo}
              amount={amount}
              currency={currency}
              icon={icon}
              tooltip={tooltip}
              disabled={disabled}
              style={style}
              size={size}
              successPage={successPage}
              failedPage={failedPage}
              entity={entity}
              entityId={entityId}
              tutionFee={tutionFee}
              hostelFee={hostelFee}
              otherFee={otherFee}
              relativePath={relativePath}
            />
          ) : paymentMode === 'offline' ? (
            <OfflinePayment
              relativePath={relativePath}
              systemId={tableSchema}
              successPage={successPage}
            />
          ) : (
            <DefermentPayment
              relativePath={relativePath}
              systemId={tableSchema}
              successPage={successPage}
            />
          )}
        </div>
      </div>
    </div>
  );
}


const OnlinePayment = ({
  enableOnline,
  enableRazorPay,
  enablePayU,
  enablePaytm,
  enableFlywire,
  enableNTT,
  enableHDFC,
  enableAMBank,
  label,
  orderNo,
  enrolmentNo,
  amount,
  currency,
  icon,
  tooltip,
  disabled,
  style,
  size,
  successPage,
  failedPage,
  entity,
  entityId,
  tutionFee,
  hostelFee,
  otherFee,
  user
}: {

  enableOnline?: boolean
  enableOffline?: boolean
  enableRazorPay?: boolean
  enablePayU?: boolean
  enablePaytm?: boolean
  enableFlywire?: boolean
  enableNTT?: boolean
  enableHDFC?: boolean
  enableAMBank?: boolean
  label?: string
  orderNo?: string
  enrolmentNo?: string
  amount?: number
  currency?: string
  icon?: string
  tooltip?: string
  disabled?: boolean
  style?: string
  size?: ControlSize
  successPage?: IPageLink
  failedPage?: IPageLink
  entity?: string
  entityId?: string
  tutionFee?: number
  hostelFee?: number
  otherFee?: number
  relativePath?: string
  systemId?: string
  className?: string
  user?: any
}) => {

  return (
    <>
      {(enableOnline) && <div className="flex-fill d-flex flex-wrap align-items-stretch gap-2 mt-5">
        {enablePaytm && <PayTmButton
          entity={entity}
          entityId={entityId}
          enrolmentNo={enrolmentNo || user?.userName || ''}
          orderNo={orderNo}
          amount={amount || 0}
          successPage={successPage || { pageMode: PageMode.View }}
          failedPage={failedPage || { pageMode: PageMode.View }}
          disabled={disabled}
          icon={icon}
          label={label}
          tooltip={tooltip}
          style={ButtonStyle.OutlinePrimary}
          size={size}
          className="border border-primary min-w-150px min-h-50px"
          currency={currency || "INR"}
        />}

        {enablePayU && <PayUButton
          className="border border-primary min-w-150px min-h-50px"
          style={ButtonStyle.OutlinePrimary}
          enrolmentNo={enrolmentNo || user?.userName || ''}
          orderNo={orderNo}
          entity={entity}
          entityId={entityId}
          firstName={user?.firstName || user?.userName}
          lastName={user?.lastName}
          email={user?.email}
          phoneNumber={user?.phoneNumber}
          successPage={successPage || { pageMode: PageMode.View }}
          failedPage={failedPage || { pageMode: PageMode.View }}
          amount={amount || 0}
          currency={currency || "INR"} />}

        {enableAMBank && <AMBankButton
          className="border border-primary min-w-150px min-h-50px"
          style={ButtonStyle.OutlinePrimary}
          enrolmentNo={enrolmentNo || user?.userName || ''}
          orderNo={orderNo}
          entity={entity}
          entityId={entityId}
          firstName={user?.firstName || user?.userName}
          lastName={user?.lastName}
          email={user?.email}
          phoneNumber={user?.phoneNumber}
          successPage={successPage || { pageMode: PageMode.View }}
          failedPage={failedPage || { pageMode: PageMode.View }}
          amount={amount || 0}
          currency={currency || "INR"} />}

        {enableRazorPay && <RazorPayButton
          className="border border-primary min-w-150px min-h-50px"
          style={ButtonStyle.OutlinePrimary}
          enrolmentNo={enrolmentNo || user?.userName || ''}
          orderNo={orderNo}
          entity={entity}
          entityId={entityId}
          firstName={user?.firstName || user?.userName}
          lastName={user?.lastName}
          email={user?.email}
          phoneNumber={user?.phoneNumber}
          successPage={successPage || { pageMode: PageMode.View }}
          failedPage={failedPage || { pageMode: PageMode.View }}
          amount={amount || 0}
          currency={currency || "INR"} />}

        {enableHDFC && <HdfcButton
          className="border border-primary min-w-150px min-h-50px"
          style={ButtonStyle.OutlinePrimary}
          enrolmentNo={enrolmentNo || user?.userName || ''}
          orderNo={orderNo}
          entity={entity}
          entityId={entityId}
          firstName={user?.firstName || user?.userName}
          lastName={user?.lastName}
          email={user?.email}
          phoneNumber={user?.phoneNumber}
          successPage={successPage || { pageMode: PageMode.View }}
          failedPage={failedPage || { pageMode: PageMode.View }}
          amount={amount || 0}
          currency={currency || "INR"} />}

        {enableNTT && <NTTButton
          className="border border-primary min-w-150px min-h-50px"
          style={ButtonStyle.OutlinePrimary}
          enrolmentNo={enrolmentNo || user?.userName || ''}
          orderNo={orderNo}
          entity={entity}
          entityId={entityId}
          firstName={user?.firstName || user?.userName}
          lastName={user?.lastName}
          email={user?.email}
          phoneNumber={user?.phoneNumber}
          successPage={successPage || { pageMode: PageMode.View }}
          failedPage={failedPage || { pageMode: PageMode.View }}
          amount={amount || 0}
          currency={currency || "INR"} />}

        {enableFlywire && <FlywireButton
          className="border border-primary min-w-150px min-h-50px"
          style={ButtonStyle.OutlinePrimary}
          enrolmentNo={enrolmentNo || user?.userName || ''}
          orderNo={orderNo}
          entity={entity}
          entityId={entityId}
          firstName={user?.firstName || user?.userName}
          lastName={user?.lastName}
          email={user?.email}
          phoneNumber={user?.phoneNumber}
          successPage={successPage || { pageMode: PageMode.View }}
          failedPage={failedPage || { pageMode: PageMode.View }}
          amount={amount || 0}
          tutionFee={tutionFee}
          hostelFee={hostelFee}
          otherFee={otherFee}
          currency={currency || "INR"} />}
      </div>}
    </>
  )
}


const OfflinePayment = ({
  systemId,
  className,
  relativePath,
  successPage,
}: {
  systemId?: string;
  className?: string;
  relativePath?: string;
  successPage?: IPageLink
}) => {
  const { user, siteId } = useAuth();
  const { data: currencyData } = useGetCurrencyCodeQuery();

  const [insertRecord] = useCreatePaymentMutation();
  const [formData, setFormData] = useState({
    amount: "",
    slcm_paidon: "",
    slcm_receiptno: "",
    attachment: null,
    payInUSD: false,
    currencyCode: "",
  });
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [documentUrl, setDocumentUrl] = useState<string>("");
  const { uploadDocument } = useUploadDocument();
  const [createRecord] = useCreateEntityChecklistItemMutation();
  const [isUploading, setIsUploading] = useState(false);
  const navigate = useNavigate();
  const [errors, setErrors] = useState({
    amount: "",
    slcm_paidon: "",
    slcm_receiptno: "",
    attachment: "",
    currencyCode: "",
  });

  const handleToggleCurrency = (value: boolean) => {
    setFormData((prev) => ({
      ...prev,
      payInUSD: value,
      currencyCode: "",
    }));
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };
  const handleUpload = (file: File): Promise<string> => {
    if (!relativePath)
      return Promise.reject('Cant upload this document.');

    setIsUploading(true);

    return uploadDocument({ file, folderRelativeUrl: relativePath })
      .then((document) => {
        return createRecord({
          entityType: systemId,
          // entityId: entityId,
          documentUrl: document.serverAbsoluteUrl || document.serverRelativeUrl,
        })
          .then(() => Promise.resolve(document.serverAbsoluteUrl))
          .catch(() => Promise.reject('Cant upload this document.'));
      })
      .catch((error) => {
        console.error(error);
        return Promise.reject(error);
      }).finally(() => setIsUploading(false));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target;
    if (files) {
      setFormData((prev) => ({
        ...prev,
        [name]: files[0],
      }));

      handleUpload(files[0])
        .then((data) => {
          setDocumentUrl(data)
          setErrors({ ...errors, [name]: "" });
        })
        .catch((error) => {
          setErrors({ ...errors, [name]: "Error uploading document" });
          console.error("Upload failed:", error);
        });
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };
  const handleCurrencyChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({
      ...prev,
      currencyCode: e.target.value,
    }));
    setErrors((prev) => ({
      ...prev,
      currencyCode: "",
    }));
  };

  const validateForm = () => {
    const newErrors: any = {};

    if (!formData.amount) newErrors.amount = "Amount is required";
    if (!formData.slcm_paidon) newErrors.slcm_paidon = "Date is required";
    if (!formData.slcm_receiptno) newErrors.slcm_receiptno = "Receipt No is required";
    if (formData.payInUSD && !formData.currencyCode)
      newErrors.currencyCode = "Currency selection is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveFormData = () => {
    if (!validateForm()) return;
    setLoading(true);
    setStatusMessage("");
    insertRecord({
      amount: formData.amount,
      date: formData.slcm_paidon,
      documentLink: documentUrl,
      referenceNo: formData.slcm_receiptno,
      rollNo: user?.memberOf.find((p) => p.entityId)?.entityId ?? "",
      isForeignCurrecny: formData.payInUSD,
      currencycode: formData.currencyCode,
    })
      .unwrap()
      .then(() => {
        if (siteId && successPage && successPage.pageId) {
          navigate(PageLinkHelper.getPageLinkUrl(siteId, successPage));
        }
      })
      .catch((err) => {
        const message =
          err.data.message ||
          "An error occurred while processing your request. Please try again later.";
        setStatusMessage(message);
        return Promise.reject(message);
      })
      .finally(() => setLoading(false));
  };

  if (!user) {
    return <></>;
  }

  return (
    <div className={clsx(className, "d-flex flex-column gap-1 my-5")}>
      <div className="container mt-3">
        <h5 className="mb-3">Offline Payment Details</h5>

        <div className="mt-3 mb-3">
          <label className="form-label fw-semibold">
            Do you want to pay in foreign currency?
          </label>
          <div className="d-flex gap-2">
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="payInUSD"
                id="payInUSDYes"
                value="yes"
                checked={formData.payInUSD}
                onChange={() => handleToggleCurrency(true)}
              />
              <label className="form-check-label" htmlFor="payInUSDYes">
                Yes
              </label>
            </div>
            <div className="form-check form-check-inline">
              <input
                className="form-check-input"
                type="radio"
                name="payInUSD"
                id="payInUSDNo"
                value="no"
                checked={!formData.payInUSD}
                onChange={() => handleToggleCurrency(false)}
              />
              <label className="form-check-label" htmlFor="payInUSDNo">
                No
              </label>
            </div>
          </div>
        </div>

        {formData.payInUSD && (
          <div className="mb-3">
            <label htmlFor="currencyCode" className="form-label fw-semibold required">
              Select Currency
            </label>
            <select
              id="currencyCode"
              className="form-select"
              value={formData.currencyCode}
              onChange={handleCurrencyChange}
            >
              <option value="">Select Currency</option>
              {currencyData && [...currencyData]
                ?.sort((a: ICurrencyEntity, b: ICurrencyEntity) => a.currencyName.localeCompare(b.currencyName))
                .map((currency: ICurrencyEntity) => (
                  <option key={currency.isoCurrencyCode} value={currency.isoCurrencyCode}>
                    {currency.currencyName}
                  </option>
                ))}
            </select>
            {errors.currencyCode && (
              <div className="text-danger">{errors.currencyCode}</div>
            )}
          </div>
        )}

        <div className="row g-3">
          <div className="col-md-4">
            <label htmlFor="amount" className="form-label fw-semibold required">
              Amount
            </label>

            <div className="d-flex align-items-center">
              {formData.payInUSD && formData.currencyCode && (
                <span className="ms-2 fw-bold mx-1 fs-4">
                  {currencyData?.find(
                    (currency: ICurrencyEntity) => currency.isoCurrencyCode === formData.currencyCode
                  )?.currencySymbol}
                </span>
              )}
              <input
                type="number"
                className="form-control"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                placeholder="Enter Amount"
                required={true}
              />
            </div>
            {errors.amount && <div className="text-danger">{errors.amount}</div>}
          </div>
          <div className="col-md-4">
            <label htmlFor="slcm_paidon" className="form-label fw-semibold required">
              Date
            </label>
            <input
              type="date"
              className="form-control"
              id="slcm_paidon"
              name="slcm_paidon"
              value={formData.slcm_paidon}
              onChange={handleChange}
              // disabled={isReadonly}
              required={true}
            />
            {errors.slcm_paidon && <div className="text-danger">{errors.slcm_paidon}</div>}
          </div>

          <div className="col-md-4">
            <label htmlFor="slcm_receiptno" className="form-label fw-semibold required">
              Receipt No
            </label>
            <input
              type="text"
              className="form-control"
              id="slcm_receiptno"
              name="slcm_receiptno"
              value={formData.slcm_receiptno}
              onChange={handleChange}
              // disabled={isReadonly}
              placeholder="Enter Receipt No"
              required={true}
            />
            {errors.slcm_receiptno && <div className="text-danger">{errors.slcm_receiptno}</div>}
          </div>
        </div>

        <div className="mt-3">
          <label htmlFor="attachment" className="form-label fw-semibold required">
            Attach Document
          </label>
          <input
            type="file"
            id="attachment"
            name="attachment"
            className="form-control"
            onChange={handleInputChange}
          // disabled={isReadonly}
          />
          {errors.attachment && <div className="text-danger">{errors.attachment}</div>}
          {isUploading && <Spinner className='me-2' show={isUploading} />}
        </div>

        <div className="d-flex align-items-end mt-4 ">
          <button
            type="button"
            onClick={saveFormData}
            disabled={loading}
            className="btn btn-lg btn-primary me-3"
          >
            <span className="indicator-label">
              {loading && <Spinner className="me-2" show={loading} />}
              <span>Confirm</span>

              <KTSVG
                path="/media/icons/duotune/arrows/arr064.svg"
                className="svg-icon-3 ms-2 me-0"
              />
            </span>
          </button>
          {statusMessage && <p className="text-success mt-3">{statusMessage}</p>}
        </div>
      </div>
    </div>
  );
};

const DefermentPayment = ({
  systemId,
  className,
  relativePath,
  successPage
}: {
  systemId?: string;
  className?: string;
  relativePath?: string;
  successPage?: IPageLink
}) => {
  const { user, siteId } = useAuth();
  const navigate = useNavigate();
  const [insertRecord] = useCreateDefermentMutation();
  const [updateRecord] = useUpdateDefermentLineMutation();
  const [deleteRecord] = useDeleteLineMutation();
  const [loading, setLoading] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string | null>(null);
  const [editIndex, setEditIndex] = useState<any | null>(null);
  const [editRowData, setEditRowData] = useState<any | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [deferment, setDeferment] = useState<IDeferment[]>([]);
  const [rowFormData, setRowFormData] = useState({
    amount: "",
    dueDate: "",
    remark: "",
  });

  const [errors, setErrors] = useState({
    amount: "",
    dueDate: "",
    remark: "",
  });

  // Validate row before adding or updating
  const validateRow = (rowData: any, originalData?: any) => {
    const newErrors: any = {};

    if (rowData.amount === "" && originalData.amount === undefined) {
      newErrors.amount = "Amount is required";
    }
    if (rowData.dueDate === "" && originalData.dueDate === undefined) {
      newErrors.dueDate = "Date is required";
    }
    if (rowData.remark === "" && originalData.remark === undefined) {
      newErrors.remark = "Receipt No is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Add a new record
  const handleAdd = async () => {
    if (!validateRow(rowFormData)) {
      setErrorMessage("Please fill in all required fields before adding.");
      return;
    }

    setErrorMessage("");
    setAddLoading(true);
    const newRecord = {
      ...rowFormData,
      rollNo: user?.memberOf.find((p) => p.entityId)?.entityId ?? "",
    };

    try {
      const result = await insertRecord({
        ...newRecord,
        status: 1,
      })
        .unwrap()
        .catch((err) => {
          setErrorMessage(err.data?.message || "An error occurred while adding.");
          throw err;
        });

      if (!result || Object.values(result).every((val) => val === null)) {
        return;
      }

      const newDeferment = Array.isArray(result) ? result[0] : result;
      setDeferment((prev) => [...prev, { ...newDeferment, id: newDeferment?.id }]);
      setRowFormData({ amount: "", dueDate: "", remark: "" });
    } catch (error) {
      console.error("Insert failed:", error);
    } finally {
      setAddLoading(false);
    }
  };


  const handleEditRow = (id: any) => {
    setEditIndex(id);
    setEditRowData({ ...deferment[id] });
  };

  const handleUpdateRow = (id: any) => {

    if (!validateRow(editRowData, deferment.find((record) => record.id === id))) return;

    const recordIndex = deferment.findIndex((record) => record.id === id);
    
    if (recordIndex !== -1) {
        const updatedRecords = [...deferment];
        updatedRecords[recordIndex] = { ...editRowData, id };
        setDeferment(updatedRecords);

        setEditIndex(null);
        setEditRowData(null);
        setSaveLoading(id);

        updateRecord({
            ...updatedRecords[recordIndex],
            status: 1,
            id: id
        })
        .unwrap()
        .then((result) => {
            setDeferment((prev) => {
                const index = prev.findIndex((record) => record.id === id);
                if (index !== -1) {
                    const updatedDeferment = [...prev];
                    updatedDeferment[index] = { ...result, id };
                    return updatedDeferment;
                }
                return [...prev, { ...result, id }];
            });
        })
        .catch((err) => {
            const message = err?.data?.message || "Error updating record";
            setErrorMessage(message);
            throw err;
        })
        .finally(() => setSaveLoading(null));
    }
};


  const handleDelete = (id: any) => {
    setDeleteLoading(id)
    deleteRecord(id)
      .unwrap()
      .then(() => {
        setDeferment((prev) => prev.filter((record) => record.id !== id));
      })
      .catch((err) => console.error("Delete Error:", err)).finally(() => setDeleteLoading(null));
  };

  const handleEditRowChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setEditRowData({
      ...editRowData,
      [name]: value,
    });
  };

  const handleRowFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRowFormData((prev) => ({
      ...prev,
      [name]: value || "",
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handleSubmit = () => {
    setLoading(true);
    Promise.all(
      deferment.map((record) =>
        updateRecord({
          id: record.id,
          status: 407450002,
        }).unwrap()
      )
    )
      .then(() => {
        setDeferment([]);
        if (siteId && successPage && successPage.pageId) {
          navigate(PageLinkHelper.getPageLinkUrl(siteId, successPage));
        }
      })
      .catch((err) => {
        console.error("Submit Error:", err);
        setErrorMessage(err.data.message || "An error occurred while processing your request.");
      })
      .finally(() => setLoading(false));
  };

  return (
    <div className={clsx(className, "d-flex flex-column gap-1 my-5")}>
      <div className="container-fluid mt-3">
        <h5 className="mb-3">Create Deferment</h5>

        {/* New Record Form */}
        <div className="row g-3 align-items-center mb-4">
          <div className="col-md-3">
            <label htmlFor="amount" className="form-label fw-semibold required">
              Amount
            </label>
            <input
              type="number"
              className="form-control"
              name="amount"
              value={rowFormData.amount}
              onChange={handleRowFormChange}
              placeholder="Amount"
              required
            />
            {errors.amount && <div className="text-danger">{errors.amount}</div>}
          </div>
          <div className="col-md-3">
            <label htmlFor="dueDate" className="form-label fw-semibold required">
              Requested Due Date
            </label>
            <input
              type="date"
              className="form-control"
              name="dueDate"
              value={String(rowFormData.dueDate)}
              onChange={handleRowFormChange}
              required
            />
            {errors.dueDate && <div className="text-danger">{errors.dueDate}</div>}
          </div>
          <div className="col-md-3">
            <label htmlFor="remark" className="form-label fw-semibold required">
              Remark
            </label>
            <input
              type="text"
              className="form-control"
              name="remark"
              value={rowFormData.remark}
              onChange={handleRowFormChange}
              placeholder="Remark"
              required
            />
            {errors.remark && <div className="text-danger">{errors.remark}</div>}
          </div>
          <div className="col-md-3 d-flex flex-column gap-2 mt-auto">
            <button className="btn btn-primary btn-md" type="button" onClick={handleAdd}>
              {addLoading ? <Spinner show={addLoading} /> :
                <>
                  <MUIIcon iconName={"ControlPoint"} className="me-1" />
                  <span>Add</span>
                </>}
            </button>
          </div>
        </div>

        {/* Records Table */}
        {deferment.length > 0 && (
          <div className="mt-4">
            {deferment.map((record) => (
              <div key={record.id} className="row align-items-center mb-2">
                <div className="col-md-3">
                  <input
                    type="number"
                    className="form-control"
                    name="amount"
                    value={
                      editIndex === record.id ? editRowData?.amount : record.amount
                    }
                    onChange={editIndex === record.id ? handleEditRowChange : undefined}
                    disabled={editIndex !== record.id}
                  />
                </div>
                <div className="col-md-3">
                  <input
                    type="date"
                    className="form-control"
                    name="dueDate"
                    value={editIndex === record.id ? editRowData?.dueDate : record.dueDate && moment(record.dueDate).format("YYYY-MM-DD")}
                    onChange={editIndex === record.id ? handleEditRowChange : undefined}
                    disabled={editIndex !== record.id}
                  />
                </div>
                <div className="col-md-3">
                  <input
                    type="text"
                    className="form-control"
                    name="remark"
                    value={
                      editIndex === record.id ? editRowData?.remark : record.remark
                    }
                    onChange={editIndex === record.id ? handleEditRowChange : undefined}
                    disabled={editIndex !== record.id}
                  />
                </div>
                <div className="col d-flex flex-row gap-2 justify-content-between ">
                  <button
                    className="btn btn-warning btn-sm "
                    type="button"
                    onClick={() => handleEditRow(record.id)}
                  >
                    <MUIIcon iconName={"Edit"} className="me-1" />
                    <span>Edit</span>
                  </button>

                  <button
                    className="btn btn-primary btn-sm"
                    type="button"
                    onClick={() => handleUpdateRow(record.id)}
                  >
                    {saveLoading === record.id ? <Spinner show={true} /> :
                      <>
                        <MUIIcon iconName={"Save"} className="me-1" />
                        <span>Save</span>
                      </>}
                  </button>

                  <button
                    className="btn btn-danger btn-sm"
                    type="button"
                    onClick={() => handleDelete(record.id)}
                  >
                    {deleteLoading === record.id ? <Spinner show={true} /> :
                      <>
                        <MUIIcon iconName={"Delete"} className="me-1" />
                        <span>Delete</span>
                      </>}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Submit Button */}
        {deferment.length > 0 && (
          <div className="mt-4 d-flex justify-content-end gap-2">
            <button className="btn btn-success" type="button" onClick={handleSubmit}>
              {loading && <Spinner className="me-2" show={loading} />}
              Submit
            </button>
          </div>
        )}
      </div>
      {errorMessage && <div className="alert alert-danger">{errorMessage}</div> }
    </div>
  );
};

Registry.addControl({
  id: "d094e594-c1fd-4d1d-b3e0-4936ac29a4c5",
  name: "Payment Button",
  type: ControlType.PaymentButtonComponent,
  properties: PaymentButtonProperties,
  component: PaymentButtonComponent
})